import { useKohdeSynkka } from "./Kohdekonteksti";
import { useLataus, LatausTila } from "./Lataus";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { naytaPvm } from "./pvm";
import { nayttonimi, selite } from "./nayttonimi";
import { Ohje } from "./Ohje";
import { api, ApiError, type Kayttaja, type Kohde, type VarastoNimike, type VarastoTapahtuma, type Tyopaketti } from "./api";
import { t, esim, virheTeksti, type KaannosAvain } from "./kieli";

/* Tuotannon varasto: määrät, saapumiset, otot, inventaario.
   Ei hintoja — fyysinen varasto per kohde. Otto voi kohdistua
   työpakettiin (materiaaliketju kiinni). Saldo = Σ tapahtumat. */

const LAJIT: readonly { avain: "saapuminen" | "otto" | "inventaario" | "korjaus"; nimi: KaannosAvain }[] = [
  { avain: "saapuminen", nimi: "var.laji.saapuminen" },
  { avain: "otto", nimi: "var.laji.otto" },
  { avain: "inventaario", nimi: "var.laji.inventaario" },
  { avain: "korjaus", nimi: "var.laji.korjaus" },
];

const num = (n: number) => n.toLocaleString("fi-FI", { maximumFractionDigits: 3 });

export function Varasto({ kayttaja }: { kayttaja: Kayttaja }) {
  /** Varaston nimikkeet ja tapahtumat: sama lista kuin /api/varasto/*. */
  const saaMuokata = kayttaja.roolit.some((r) =>
    ["paakayttaja", "suunnittelija", "tyonjohtaja"].includes(r))
    // T488: oikeus `materiaalit` (koko yritys tai kohde) — palvelin tarkistaa kohteen.
    || (kayttaja.oikeudet.includes("materiaalit") || (kayttaja.kohdeOikeudet?.["materiaalit"] ?? []).length > 0);

  /*
   * SharePoint-synkka on eri oikeus kuin varaston käyttö, ja tarkoituksella
   * tiukempi: `POST /api/varasto/synkka` on paakayttaja + suunnittelija.
   *
   * Ei löysätty API:a vastaamaan selainta vaan päinvastoin. Synkka on
   * Pekilo-projektin väliaikainen silta jotta testidata saatiin liikkeelle
   * — ei tuotteen ominaisuus. Sampo on oma totuutensa, ei peilaus toisesta
   * järjestelmästä (PAATOKSET §Sampo on oma totuutensa), eikä oikeutta
   * kannata avata ominaisuuteen jonka ei ole tarkoitus jäädä.
   */
  const saaSynkata = kayttaja.roolit.some((r) =>
    ["paakayttaja", "suunnittelija"].includes(r));
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [kohde, setKohde] = useState<number | "">("");
  // T426: yläpalkin kohde ohjaa. «Kaikki kohteet» ei kelpaa tälle näkymälle, joten silloin näkymä pitää oman oletuksensa
  // ja valitsimensa eikä työnnä arvaustaan yläpalkkiin.
  const { ylapalkissa, ylaValittu } = useKohdeSynkka(kohde, setKohde, { kaikkiSallittu: false });
  const [nimikkeet, setNimikkeet] = useState<VarastoNimike[]>([]);
  const [paketit, setPaketit] = useState<Tyopaketti[]>([]);
  const [haku, setHaku] = useState("");
  const [avattu, setAvattu] = useState<number | null>(null);
  const [uusiAuki, setUusiAuki] = useState(false);
  const [lataa, setLataa] = useState(false);
  const [synkkaa, setSynkkaa] = useState(false);
  const [synkkaViesti, setSynkkaViesti] = useState<string | null>(null);
  const [graphKaytossa, setGraphKaytossa] = useState(false);

  async function synkkaaSP() {
    if (kohde === "") return;
    setSynkkaa(true); setSynkkaViesti(null);
    try {
      const r = await api.synkkaaVarasto(kohde);
      setSynkkaViesti(t("var.synkattu", { p: r.paivitetty, l: r.luettu }));
      paivita();
    } catch (e) {
      setSynkkaViesti(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : e instanceof Error ? e.message : t("var.synkka_virhe"));
    } finally { setSynkkaa(false); }
  }

  const klKohteet = useLataus();
  useEffect(() => {
    klKohteet.seuraa(api.kohteet().then((k) => { setKohteet(k); setKohde((v) => (v === "" && k.length ? k[0]!.id : v)); }));
    api.varastoSynkkaTila().then((t) => setGraphKaytossa(t.konffattu)).catch(() => setGraphKaytossa(false));
  }, [klKohteet.kerta]);

  // Päätös 91 (Toni 6.10.2026): vain viimeisimmän haun vastaus hyväksytään — vanha vastaus toisesta kohteesta ei saa
  // tuoda työpaketteja ottolomakkeeseen (palvelin kieltäytyy myös: tyopaketti_eri_kohteessa).
  const viimeisin = useRef(0);
  const paivita = () => {
    const n = ++viimeisin.current; const tuore = () => n === viimeisin.current;
    if (kohde === "") { setNimikkeet([]); return; }
    setLataa(true);
    api.varasto(kohde).then((r) => { if (tuore()) setNimikkeet(r); }).catch(() => { if (tuore()) setNimikkeet([]); })
      .finally(() => { if (tuore()) setLataa(false); });
    api.tyopaketit(kohde).then((r) => { if (tuore()) setPaketit(r); }).catch(() => { if (tuore()) setPaketit([]); });
  };
  useEffect(paivita, [kohde]);

  const suod = nimikkeet.filter((n) =>
    !haku.trim() || `${n.tunnus} ${n.nimi} ${n.materiaali ?? ""} ${n.ryhma ?? ""}`.toLowerCase().includes(haku.toLowerCase()));

  return (
    <>
      <header className="head">
        <div className="head__rivi"><h1>{t("valikko.varasto")}</h1></div>
      </header>
      <LatausTila l={klKohteet} nimi="lataus.kohteet" />

      {/* Ohje ylös (Toni 24.9.2026). «Kuka» = /api/varasto*: luku vaadi + moduuli + kohderajaus, kirjoitus
          vaadiRooli(paakayttaja, suunnittelija, tyonjohtaja), synkka vaadiRooli(paakayttaja, suunnittelija).
          Saldon kaava routes.ts GET /api/varasto; alalaidan vajaa selite korvattiin tällä. */}
      <Ohje
        kuka={<>{t("var.ohje.kuka")}</>}
        luvut={<>{t("var.ohje.luvut")}</>} />

      <div className="suodatinrivi">
        {!(ylapalkissa && ylaValittu != null) && <label className="dim" >{t("tyo.kohde_iso")}{" "}
          <select value={kohde} onChange={(e) => setKohde(e.target.value ? Number(e.target.value) : "")}>
            <option value="">{t("var.valitse")}</option>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
          </select>
        </label>}
        <input value={haku} onChange={(e) => setHaku(e.target.value)} placeholder={t("var.hae")} style={{ minWidth: 180 }} />
        {saaMuokata && kohde !== "" && (
          <button onClick={() => setUusiAuki(!uusiAuki)}>{uusiAuki ? t("hl.sulje") : t("var.uusi")}</button>
        )}
        {saaSynkata && kohde !== "" && graphKaytossa && (
          <button className="toissijainen" disabled={synkkaa} onClick={synkkaaSP}
            title={t("var.synkka_title")}>
            {synkkaa ? t("var.synkataan") : t("var.synkkaa")}
          </button>
        )}
      </div>
      {synkkaViesti && <p className="dim" style={{ margin: "-8px 0 12px" }}>{synkkaViesti}</p>}

      {uusiAuki && kohde !== "" && (
        <UusiNimike kohde_id={kohde} onLuotu={() => { setUusiAuki(false); paivita(); }} />
      )}

      {lataa && <p className="dim">{t("ltar.ladataan")}</p>}
      {!lataa && kohde !== "" && suod.length === 0 && (
        <p className="dim">{t("var.ei_nimikkeita")} {saaMuokata && t("var.luo_ensimmainen")}</p>
      )}

      {suod.length > 0 && (
        <section className="panel scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("var.tunnus")}</th><th className="th--vasen">{t("var.tuote")}</th><th>{t("var.ryhma")}</th>
                <th>DN</th><th>{t("var.mitat")}</th><th>{t("var.materiaali")}</th>
                <th style={{ textAlign: "right" }}>{t("tyo.saldo")}</th><th></th><th></th>
              </tr>
            </thead>
            <tbody>
              {suod.map((n) => (
                <Rivi key={n.id} n={n} auki={avattu === n.id} paketit={paketit}
                  saaMuokata={saaMuokata}
                  avaa={() => setAvattu(avattu === n.id ? null : n.id)} onMuutos={paivita} />
              ))}
            </tbody>
          </table>
        </section>
      )}

    </>
  );
}

/* ── yksi nimike + tapahtumat ─────────────────────────────── */
function Rivi({ n, auki, paketit, saaMuokata, avaa, onMuutos }:
  { n: VarastoNimike; auki: boolean; paketit: Tyopaketti[]; saaMuokata: boolean;
    avaa: () => void; onMuutos: () => void }) {
  const [tap, setTap] = useState<VarastoTapahtuma[] | null>(null);

  useEffect(() => {
    if (auki) api.varastoTapahtumat(n.id).then(setTap).catch(() => setTap([]));
    else setTap(null);
  }, [auki, n.id]);

  const vahainen = n.saldo <= 0;
  return (
    <>
      <tr className={auki ? "tr--auki" : ""}>
        <td className="mono">{n.tunnus}</td>
        <td className="td--vasen">{n.nimi}</td>
        <td>{n.ryhma ?? "—"}</td>
        <td className="mono">{n.dn ?? "—"}</td>
        <td className="mono">{n.mitat ?? "—"}</td>
        <td>{n.materiaali ?? "—"}</td>
        <td className="mono" style={{ textAlign: "right", fontWeight: 700, color: vahainen ? "var(--korostus)" : undefined }}>
          {num(n.saldo)} {n.yksikko}
          {n.synkka_saldo != null && (
            <span className="dim" title={`${t("var.sp_title")}${n.synkka_aika ? " · " + n.synkka_aika.slice(0, 16).replace("T", " ") : ""}`}
              style={{ fontSize: 9, marginLeft: 4, verticalAlign: "super" }}>SP</span>
          )}
        </td>
        <td className="td--oikea">
          <button className="nappi nappi--kevyt" onClick={avaa} aria-expanded={auki}>
            {auki ? t("hl.sulje") : t("var.tapahtumat")}
          </button>
        </td>
        <td>
          {saaMuokata && (
            <button className="nappi nappi--kevyt" title={t("var.poista_title")}
              onClick={() => { if (confirm(t("var.poista_kysymys", { tunnus: n.tunnus, nimi: n.nimi }))) api.poistaVarastoNimike(n.id).then(onMuutos); }}>×</button>
          )}
        </td>
      </tr>
      {auki && (
        <tr className="tr--laajennus">
          <td colSpan={9}>
            <div className="laajennus">
              {saaMuokata && <TapahtumaLomake nimike={n} paketit={paketit} onKirjattu={() => { onMuutos(); api.varastoTapahtumat(n.id).then(setTap); }} />}
              <h4 style={{ margin: "12px 0 6px", fontSize: 13 }}>{t("var.tapahtumat")}</h4>
              {tap === null && <p className="dim">{t("ltar.ladataan")}</p>}
              {tap && tap.length === 0 && <p className="dim">{t("var.ei_tapahtumia")}</p>}
              {tap && tap.length > 0 && (
                <table className="tbl" style={{ fontSize: 12.5 }}>
                  <thead><tr><th>{t("ltar.pvm")}</th><th>{t("var.laji")}</th><th style={{ textAlign: "right" }}>{t("var.maara")}</th><th>{t("var.viite")}</th><th>{t("var.sulatus")}</th><th>{t("var.kirjaaja")}</th><th></th></tr></thead>
                  <tbody>
                    {tap.map((t) => (
                      <tr key={t.id}>
                        <td className="mono">{naytaPvm(t.pvm)}</td>
                        <td title={selite("varasto_laji", t.laji)}>{nayttonimi("varasto_laji", t.laji)}</td>
                        <td className="mono" style={{ textAlign: "right", color: t.maara < 0 ? "var(--korostus)" : undefined }}>
                          {t.maara > 0 ? "+" : ""}{num(t.maara)}
                        </td>
                        <td>{t.tp_nro ? `#${t.tp_nro} ${t.tp_nimi}` : (t.viite ?? "—")}</td>
                        <td className="mono">{t.sulatus ?? "—"}</td>
                        <td>{t.kirjaaja ?? "—"}</td>
                        <td>{saaMuokata && <button className="nappi nappi--kevyt" onClick={() => api.poistaVarastoTapahtuma(t.id).then(() => { onMuutos(); api.varastoTapahtumat(n.id).then(setTap); })}>×</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── tapahtuman kirjaus ───────────────────────────────────── */
function TapahtumaLomake({ nimike, paketit, onKirjattu }:
  { nimike: VarastoNimike; paketit: Tyopaketti[]; onKirjattu: () => void }) {
  const [laji, setLaji] = useState<"saapuminen" | "otto" | "inventaario" | "korjaus">("saapuminen");
  const [maara, setMaara] = useState("");
  const [pvm, setPvm] = useState("");
  const [viite, setViite] = useState("");
  const [sulatus, setSulatus] = useState("");
  const [tp, setTp] = useState<number | "">("");
  const [tallentaa, setTallentaa] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);

  async function kirjaa() {
    const m = Number(maara.replace(",", "."));
    if (!Number.isFinite(m)) { setVirhe(t("var.anna_maara")); return; }
    setTallentaa(true); setVirhe(null);
    try {
      await api.kirjaaVarastoTapahtuma({
        nimike_id: nimike.id, laji, maara: m,
        ...(pvm && { pvm }),
        ...(laji === "otto" && tp !== "" && { tyopaketti_id: tp }),
        ...(viite.trim() && { viite: viite.trim() }),
        ...(sulatus.trim() && { sulatus: sulatus.trim() }),
      });
      setMaara(""); setViite(""); setSulatus(""); setTp("");
      onKirjattu();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : e instanceof Error ? e.message : t("var.kirjaus_virhe")); }
    finally { setTallentaa(false); }
  }

  const kentta: CSSProperties = { display: "flex", flexDirection: "column", fontSize: 11 };
  return (
    <div style={{ background: "var(--pelti)", border: "1px solid var(--viiva)", padding: 12 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={kentta}>{t("var.laji")}
          <select value={laji} onChange={(e) => setLaji(e.target.value as typeof laji)}>
            {LAJIT.map((l) => <option key={l.avain} value={l.avain}>{t(l.nimi)}</option>)}
          </select>
        </label>
        <label style={kentta}>{laji === "inventaario" ? t("var.laskettu_maara") : t("var.maara")}
          <input value={maara} onChange={(e) => setMaara(e.target.value)} inputMode="decimal" style={{ width: 90 }} placeholder={nimike.yksikko} />
        </label>
        <label style={kentta}>{t("ltar.pvm")}
          <input type="date" value={pvm} onChange={(e) => setPvm(e.target.value)} />
        </label>
        {laji === "otto" && (
          <label style={kentta}>{t("var.tyopaketti")}
            <select value={tp} onChange={(e) => setTp(e.target.value ? Number(e.target.value) : "")}>
              <option value="">{t("var.ei_pakettia")}</option>
              {paketit.map((p) => <option key={p.id} value={p.id}>#{p.nro} {p.nimi}</option>)}
            </select>
          </label>
        )}
        {laji === "saapuminen" && (
          <>
            <label style={kentta}>{t("var.toimittaja")}
              <input value={viite} onChange={(e) => setViite(e.target.value)} placeholder={esim("Tukku Oy / RK-123")} style={{ width: 150 }} />
            </label>
            <label style={kentta}>{t("var.sulatusnro")}
              <input value={sulatus} onChange={(e) => setSulatus(e.target.value)} placeholder={t("var.sulatus_vihje")} style={{ width: 110 }} />
            </label>
          </>
        )}
        <button className="nappi nappi--paa" disabled={tallentaa || !maara.trim()} onClick={kirjaa}>
          {tallentaa ? t("hl.kirjataan") : t("var.kirjaa")}
        </button>
      </div>
      {virhe && <p className="huom huom--vika" role="alert" style={{ marginTop: 6 }}>{virhe}</p>}
    </div>
  );
}

/* ── uusi nimike ──────────────────────────────────────────── */
function UusiNimike({ kohde_id, onLuotu }: { kohde_id: number; onLuotu: () => void }) {
  const [tunnus, setTunnus] = useState("");
  const [nimi, setNimi] = useState("");
  const [ryhma, setRyhma] = useState("");
  const [dn, setDn] = useState("");
  const [mitat, setMitat] = useState("");
  const [materiaali, setMateriaali] = useState("");
  const [yksikko, setYksikko] = useState("kpl");
  const [tallentaa, setTallentaa] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);

  async function tallenna() {
    if (!tunnus.trim() || !nimi.trim()) { setVirhe(t("var.tunnus_tuote_vaaditaan")); return; }
    setTallentaa(true); setVirhe(null);
    try {
      await api.luoVarastoNimike({
        kohde_id, tunnus: tunnus.trim(), nimi: nimi.trim(),
        ryhma: ryhma.trim() || null, dn: dn.trim() ? Number(dn) : null,
        mitat: mitat.trim() || null, materiaali: materiaali.trim() || null,
        yksikko: yksikko.trim() || "kpl",
      });
      onLuotu();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : e instanceof Error ? e.message : t("tk.tallennus_virhe")); }
    finally { setTallentaa(false); }
  }

  const k: CSSProperties = { display: "flex", flexDirection: "column", fontSize: 11 };
  return (
    <div style={{ background: "var(--pelti)", border: "1px solid var(--viiva)", padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={k}>{t("var.tunnus")}<input value={tunnus} onChange={(e) => setTunnus(e.target.value)} placeholder="V001" style={{ width: 80 }} /></label>
        <label style={k}>{t("var.tuote")}<input value={nimi} onChange={(e) => setNimi(e.target.value)} placeholder={t("var.tuote_esim")} style={{ width: 140 }} /></label>
        <label style={k}>{t("var.ryhma")}<input value={ryhma} onChange={(e) => setRyhma(e.target.value)} placeholder={t("valikko.varasto")} style={{ width: 100 }} /></label>
        <label style={k}>DN<input value={dn} onChange={(e) => setDn(e.target.value)} inputMode="numeric" style={{ width: 60 }} /></label>
        <label style={k}>{t("var.mitat")}<input value={mitat} onChange={(e) => setMitat(e.target.value)} placeholder="88,9x2" style={{ width: 90 }} /></label>
        <label style={k}>{t("var.materiaali")}<input value={materiaali} onChange={(e) => setMateriaali(e.target.value)} placeholder="1.4404" style={{ width: 90 }} /></label>
        <label style={k}>{t("var.yksikko")}<input value={yksikko} onChange={(e) => setYksikko(e.target.value)} style={{ width: 60 }} /></label>
        <button className="nappi nappi--paa" disabled={tallentaa} onClick={tallenna}>{tallentaa ? "…" : t("var.luo")}</button>
      </div>
      {virhe && <p className="huom huom--vika" role="alert" style={{ marginTop: 6 }}>{virhe}</p>}
    </div>
  );
}
